import { useState } from 'react';
import { Color, Size, criteriaProblems, t, type CriteriaRow } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { ViewCriteriaRows, type CriteriaAttribute, type CriteriaColumn } from '../../components/modals/ViewCriteriaRows';
import { ATTRIBUTE_TYPES_PATH } from '../settings/AttributeTypeForm';
import { useApi } from './api';
import styles from './ItemFilterWindow.module.css';

/** A catalog attribute type as Settings lists it, with its values, and the families beside it holding each Measured type's units (decision 0285). */
type AttributeTypeAnswer = {
  rows: { id: string; attributeName: string; kind: string; isActive: boolean; unitFamilyId: string | null; defaultUnitId: string | null; values: { id: string; value: string; isActive: boolean }[] }[];
  families: { id: string; units: { id: string; label: string }[] }[];
};

/** The catalog's Measured and Enumerated types as the condition rows read them, each Measured one with its family's units. */
export function criteriaAttributesOf(answer: AttributeTypeAnswer): CriteriaAttribute[] {
  return answer.rows.flatMap((row): CriteriaAttribute[] => {
    if (row.kind !== 'Measured' && row.kind !== 'Enumerated') return [];
    const units = row.kind === 'Measured' ? answer.families.find((family) => family.id === row.unitFamilyId)?.units ?? [] : [];
    return [{ id: row.id, name: row.attributeName, kind: row.kind, isActive: row.isActive, defaultUnitId: row.defaultUnitId, units, values: row.values.map((value) => ({ id: value.id, label: value.value, isActive: value.isActive })) }];
  });
}

/**
 * 🔒 The catalog's attribute types for the Items list's condition rows (decision 8800), read from the
 * route the Edit Selected window reads them from, and only while a window that offers them is open.
 */
export function useCriteriaAttributes(apiBaseUrl: string, wanted: boolean): CriteriaAttribute[] | undefined {
  const read = useApi<AttributeTypeAnswer>(wanted ? ATTRIBUTE_TYPES_PATH : null, apiBaseUrl);
  return read.state.kind === 'ready' ? criteriaAttributesOf(read.state.data) : undefined;
}

export interface ItemFilterProps {
  /** The conditions standing, as the address carries them. */
  conditions: readonly CriteriaRow[];
  columns: readonly CriteriaColumn[];
  attributes?: readonly CriteriaAttribute[] | undefined;
  /** The list's plural, which the window's title names. */
  plural: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Narrows the list by these conditions now, saving nothing; an empty list clears it. */
  onApply: (rows: readonly CriteriaRow[]) => void;
  /** Opens the New View window with these conditions in its rows. */
  onSaveAsView: (rows: readonly CriteriaRow[]) => void;
}

/**
 * 🔒 The Items list's Filter (decision 8800; T3276): a button on the filter row, reading the count of
 * conditions standing, a Clear beside it while any stand, and a window holding the New View window's
 * own condition rows. Apply narrows the list at once and saves nothing; Save as View hands the rows
 * to the New View window. The window is mounted only while open, so it opens on the conditions standing.
 */
export function ItemFilter({ conditions, columns, attributes, plural, open, onOpen, onClose, onApply, onSaveAsView }: ItemFilterProps) {
  const count = conditions.length;
  const label = count > 0 ? `${t('listFilter.button')} · ${count}` : t('listFilter.button');
  return (
    <div className={styles.filter} data-list-conditions={count}>
      <Button size={Size.Small} color={count > 0 ? Color.Navy : Color.Inactive} text={[label]} actions={[onOpen]} pressed={open} />
      {count > 0 ? <Button size={Size.Small} color={Color.Inactive} text={[t('listFilter.clearConditions')]} actions={[() => onApply([])]} /> : null}
      {open ? (
        <FilterWindow conditions={conditions} columns={columns} attributes={attributes} plural={plural} onClose={onClose} onApply={onApply} onSaveAsView={onSaveAsView} />
      ) : null}
    </div>
  );
}

function FilterWindow({ conditions, columns, attributes, plural, onClose, onApply, onSaveAsView }: Pick<ItemFilterProps, 'conditions' | 'columns' | 'attributes' | 'plural' | 'onClose' | 'onApply' | 'onSaveAsView'>) {
  const [rows, setRows] = useState<readonly CriteriaRow[]>(conditions);
  const [checked, setChecked] = useState(false);
  const problems = criteriaProblems(rows);
  /** 🔒 A blank condition is refused with its row marked (decision 2172), at Apply as at Save as View. */
  const settled = (act: (rows: readonly CriteriaRow[]) => void) => () => {
    setChecked(true);
    if (Object.keys(problems).length > 0) return;
    act(rows);
    onClose();
  };
  return (
    <Modal
      open
      title={[t('listFilter.button'), plural].join(' ')}
      onCancel={onClose}
      width="wide"
      dataModal="list-filter"
      actions={[
        { kind: 'cancel', label: t('modal.cancel'), onAct: onClose },
        { kind: 'primary', label: t('listFilter.apply'), onAct: settled(onApply) },
      ]}
    >
      <section className={styles.band} data-list-filter-rows="true">
        <h3 className={styles.heading}>{t('view.criteria')}</h3>
        <p className={styles.hint}>{t('listFilter.hint')}</p>
        <ViewCriteriaRows rows={rows} columns={columns} {...(attributes ? { attributes } : {})} checked={checked} onRows={(next) => setRows((was) => next(was))} />
        <div className={styles.acts}>
          <span data-list-filter-save-view="true">
            <Button size={Size.Small} color={Color.Navy} text={[t('listFilter.saveAsView')]} actions={[settled((typed) => { onApply(typed); onSaveAsView(typed); })]} disabled={rows.length === 0} disabledReason={t('listFilter.saveNeedsRows')} />
          </span>
          <span data-list-filter-clear="true">
            <Button size={Size.Small} color={Color.Inactive} text={[t('listFilter.clear')]} actions={[() => { onApply([]); onClose(); }]} />
          </span>
        </div>
      </section>
    </Modal>
  );
}
